import { Form } from 'antd';

import FormItem from '@/components/form/FormItem';

import { renderFieldControl } from './fieldRegistry';

import type { FormSizeTokens } from './sizing';
import type { FieldConfig } from './types';
import type { ReactNode } from 'react';

function toFormItemLabel(label: FieldConfig['label']): ReactNode | undefined {
  return label;
}

function normalizeNumberFieldValue(value: unknown): unknown {
  if (value === '' || value === undefined) {
    return null;
  }
  return value;
}

function getValuePropName(field: FieldConfig) {
  if (field.valuePropName != null) {
    return field.valuePropName;
  }
  if (field.type === 'checkbox') {
    return 'checked';
  }
  return undefined;
}

function getFormItemLabel(field: FieldConfig): ReactNode | undefined {
  if (field.type === 'checkbox') {
    return undefined;
  }
  return toFormItemLabel(field.label);
}

export function evaluateHidden(
  hidden: boolean | ((values: Record<string, unknown>) => boolean) | undefined,
  values: Record<string, unknown>,
): boolean {
  if (hidden === undefined) {
    return false;
  }
  if (typeof hidden === 'function') {
    return hidden(values);
  }
  return hidden;
}

export function getFieldKey(field: FieldConfig): string {
  return (
    field.key ??
    (Array.isArray(field.name)
      ? field.name.join('.')
      : String(field.name ?? field.type))
  );
}

export function SchemaField({
  field,
  tokens,
  formValues,
}: {
  field: FieldConfig;
  tokens: FormSizeTokens;
  formValues: Record<string, unknown>;
}) {
  const form = Form.useFormInstance();

  if (evaluateHidden(field.hidden, formValues)) {
    return null;
  }

  const control = renderFieldControl(field, {
    form,
    field,
    inputSize: tokens.inputSize,
  });

  if (field.type === 'custom' && field.name == null) {
    if (field.label != null || field.help != null || field.tooltip != null) {
      return (
        <FormItem
          label={toFormItemLabel(getFormItemLabel(field))}
          tooltip={field.tooltip}
          help={field.help}
          validateStatus={field.validateStatus}
        >
          {control}
        </FormItem>
      );
    }
    return control;
  }

  if (field.type === 'list') {
    if (field.label != null || field.help != null || field.tooltip != null) {
      return (
        <FormItem
          label={toFormItemLabel(field.label)}
          tooltip={field.tooltip}
          help={field.help}
          validateStatus={field.validateStatus}
        >
          {control}
        </FormItem>
      );
    }
    return control;
  }

  if (field.type === 'file' || field.type === 'files') {
    return control;
  }

  if (field.type === 'hidden') {
    return (
      <FormItem name={field.name} hidden>
        {control}
      </FormItem>
    );
  }

  return (
    <FormItem
      label={toFormItemLabel(getFormItemLabel(field))}
      name={field.name}
      tooltip={field.tooltip}
      help={field.help}
      validateStatus={field.validateStatus}
      valuePropName={getValuePropName(field)}
      normalize={
        field.type === 'number' ? normalizeNumberFieldValue : undefined
      }
      required={field.required}
    >
      {control}
    </FormItem>
  );
}
